import { useRef, type KeyboardEvent } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

interface Props {
  label?: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  hint?: string;
  suffix?: string;
}

/**
 * Dark-theme number input with stacked +/- buttons in the trailing zone
 * (matches the eagle-eye monitor pattern, KAF accent).
 *
 * Native browser spinners are hidden via global CSS in styles/index.css
 * (input[type=number] webkit + moz appearance overrides).
 */
export default function NumberInput({
  label, value, onChange, min, max, step = 1, placeholder,
  disabled, size = 'md', className, hint, suffix,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  function clamp(n: number): number {
    let v = Number.isFinite(n) ? n : 0;
    if (typeof min === 'number') v = Math.max(min, v);
    if (typeof max === 'number') v = Math.min(max, v);
    return v;
  }

  function bump(delta: number) {
    if (disabled) return;
    onChange(clamp(value + delta));
    // keep focus on the input so keyboard nav still works
    inputRef.current?.focus();
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowUp')   { e.preventDefault(); bump(step); }
    if (e.key === 'ArrowDown') { e.preventDefault(); bump(-step); }
  }

  const heightClass = size === 'sm' ? 'h-8' : 'h-10';
  const fontClass   = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <div className={cn('w-full', className)}>
      {label && <label className="block text-xs text-muted mb-1.5">{label}</label>}
      <div
        className={cn(
          'relative flex items-stretch w-full rounded-lg border bg-bg overflow-hidden transition',
          heightClass,
          disabled
            ? 'opacity-50 cursor-not-allowed border-slate-800'
            : 'border-slate-800 hover:border-slate-700 focus-within:border-accent-400 focus-within:ring-1 focus-within:ring-accent-400/40'
        )}
      >
        <input
          ref={inputRef}
          type="number"
          inputMode="numeric"
          value={Number.isFinite(value) ? value : 0}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '' || raw === '-') { onChange(0); return; }
            const n = Number(raw);
            onChange(clamp(n));
          }}
          onKeyDown={onKey}
          className={cn(
            'flex-1 bg-transparent px-3 outline-none text-muted-strong placeholder:text-muted/50',
            fontClass,
            'no-spinner',
          )}
        />
        {suffix && (
          <span className={cn('flex items-center pr-2 text-muted', fontClass)}>{suffix}</span>
        )}
        <div className="flex flex-col w-7 border-l border-slate-800 shrink-0">
          <button
            type="button"
            onClick={() => bump(step)}
            disabled={disabled || (typeof max === 'number' && value >= max)}
            className="flex-1 flex items-center justify-center text-muted hover:text-accent-400 hover:bg-slate-800/40 active:bg-slate-800/70 transition disabled:opacity-40 disabled:cursor-not-allowed"
            tabIndex={-1}
            aria-label="Increment"
            data-compact
          >
            <ChevronUp size={11} />
          </button>
          <div className="border-t border-slate-800" />
          <button
            type="button"
            onClick={() => bump(-step)}
            disabled={disabled || (typeof min === 'number' && value <= min)}
            className="flex-1 flex items-center justify-center text-muted hover:text-accent-400 hover:bg-slate-800/40 active:bg-slate-800/70 transition disabled:opacity-40 disabled:cursor-not-allowed"
            tabIndex={-1}
            aria-label="Decrement"
            data-compact
          >
            <ChevronDown size={11} />
          </button>
        </div>
      </div>
      {hint && <p className="mt-1 text-[11px] text-muted">{hint}</p>}
    </div>
  );
}
